import React, { useState, useEffect } from 'react';
import supabase from '../supabase/supabase';
import SafeIcon from '../common/SafeIcon';
import { FiUploadCloud } from 'react-icons/fi';

export default function AdminBulkAIUpload({ onUploadComplete }) {
  const [categories, setCategories] = useState([]);
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [progressText, setProgressText] = useState('');
  const [msg, setMsg] = useState('');

  useEffect(() => {
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    const { data } = await supabase.from('categories_1715000000').select('*');
    if (data) setCategories(data);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!files || files.length === 0) return setMsg('Please select files to upload.');
    
    const apiKey = import.meta.env.VITE_OPENAI_API_KEY;
    if (!apiKey) {
      return setMsg("OpenAI API key missing! Please ensure it is set.");
    }

    setLoading(true);
    setMsg('');
    const fileArray = Array.from(files);
    
    try {
      let successCount = 0;
      const categoryNames = categories.map(c => c.name).join(', ');

      for (let i = 0; i < fileArray.length; i++) {
        const file = fileArray[i];
        setProgressText(`Processing image ${i + 1} of ${fileArray.length}...`);

        const fileExt = file.name.split('.').pop();
        const fileName = `${Date.now()}_${Math.random()}.${fileExt}`;
        
        const { error: uploadError } = await supabase.storage
          .from('portfolio_images')
          .upload(fileName, file);
        
        if (uploadError) throw uploadError;
        
        const { data: { publicUrl } } = supabase.storage
          .from('portfolio_images')
          .getPublicUrl(fileName);

        setProgressText(`AI Analyzing image ${i + 1}...`);
        
        const prompt = `Analyze this image. 1. Identify what category it belongs to from this exact list: [${categoryNames}]. If it doesn't fit perfectly into any, return "Uncategorized". 2. Generate exactly 3 relevant hashtags describing the visual elements and text in the image (without the # symbol). Ensure the hashtags you generate are highly relevant to the business niche of the category you just selected. Return ONLY exactly this JSON format: {"category": "Category Name", "tags": ["tag1", "tag2", "tag3"]}`;
        
        const response = await fetch('https://api.openai.com/v1/chat/completions', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${apiKey}`
          },
          body: JSON.stringify({
            model: 'gpt-4o',
            response_format: { type: "json_object" },
            messages: [
              {
                role: 'user',
                content: [
                  { type: 'text', text: prompt },
                  { type: 'image_url', image_url: { url: publicUrl } }
                ]
              }
            ],
            max_tokens: 300
          })
        });

        const aiData = await response.json();
        if (aiData.error) throw new Error(aiData.error.message);

        const aiResult = JSON.parse(aiData.choices[0].message.content);
        
        let matchedCategoryId = null;
        if (aiResult.category && aiResult.category !== "Uncategorized") {
          const cat = categories.find(c => c.name.toLowerCase() === aiResult.category.toLowerCase());
          if (cat) matchedCategoryId = cat.id;
        }

        const title = file.name.split('.')[0].replace(/[-_]/g, ' '); 
        
        const { error: dbError } = await supabase.from('posts_1715000000').insert([{
          title: title,
          category_id: matchedCategoryId,
          image_url: publicUrl,
          tags: aiResult.tags || []
        }]);

        if (dbError) throw dbError;
        successCount++;
      }

      setMsg(`BOOM! ${successCount} graphics auto-categorized & tagged!`);
      setFiles([]);
      document.getElementById('ai-file-upload').value = '';
      if (onUploadComplete) onUploadComplete();
      
    } catch (error) {
      setMsg(`Error: ${error.message}`);
    } finally {
      setLoading(false);
      setProgressText('');
    }
  };

  return (
    <div className="bg-white p-8 rounded-3xl shadow-retro border-4 border-retro-brown">
      <div className="flex items-center gap-4 mb-8 bg-retro-cream p-4 border-4 border-retro-brown rounded-2xl shadow-retro-sm inline-flex">
        <div className="w-14 h-14 rounded-full bg-retro-blue border-2 border-retro-brown flex items-center justify-center text-white shadow-retro-sm">
          <SafeIcon icon={FiUploadCloud} className="text-2xl font-bold" />
        </div>
        <div>
          <h2 className="font-display text-2xl tracking-wider text-retro-brown uppercase">AI BULK MAGIC</h2>
          <p className="font-bold text-retro-brown/70">Upload & let the robot work.</p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="grid grid-cols-1 gap-6">
        <div className="relative border-4 border-dashed border-retro-brown/40 rounded-2xl p-10 hover:bg-retro-cream transition-colors text-center cursor-pointer">
          <input 
            id="ai-file-upload" 
            type="file" 
            accept="image/*" 
            multiple 
            onChange={(e) => setFiles(e.target.files)}
            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
            required 
          />
          <h3 className="font-display text-2xl text-retro-brown mb-2">DROP FILES HERE</h3>
          <p className="font-bold text-retro-brown/70">PNG, JPG, WEBP</p>
          
          {files.length > 0 && (
            <div className="mt-4 inline-block bg-retro-yellow border-4 border-retro-brown px-6 py-2 rounded-full shadow-retro-sm">
              <p className="text-lg text-retro-brown font-black">{files.length} READY</p>
            </div>
          )}
        </div>

        <button 
          type="submit" 
          disabled={loading} 
          className="w-full bg-retro-blue text-white py-5 rounded-xl font-display text-2xl tracking-wider border-4 border-retro-brown shadow-retro hover:shadow-retro-hover hover:-translate-y-1 active:translate-y-1 active:shadow-retro-active transition-all disabled:opacity-50 flex items-center justify-center gap-3"
        >
          <SafeIcon icon={FiUploadCloud} />
          {loading ? 'PROCESSING...' : 'RUN AI MAGIC'}
        </button>
        
        {progressText && (
          <div className="bg-retro-teal text-white border-4 border-retro-brown p-4 rounded-xl shadow-retro-sm text-center animate-pulse">
            <p className="text-lg font-bold uppercase tracking-wide">{progressText}</p>
          </div>
        )}
        
        {msg && (
          <div className={`text-lg font-bold text-center p-4 border-4 border-retro-brown rounded-xl shadow-retro-sm ${msg.toLowerCase().includes('error') ? 'bg-red-100 text-red-700' : 'bg-retro-lime text-retro-brown'}`}>
            {msg}
          </div>
        )}
      </form>
    </div>
  );
}